import { onMounted, shallowRef } from 'vue'
import { useAppParams } from '@/stores/app'
import { getVersionInfo, type VersionInfoData } from '@/api/common'
import { compareVersions } from '@/utils/version'

/** 版本弹窗所需数据（检测逻辑填充后传给 NewVersionModal） */
export interface VersionData {
  version: string
  url: string
  /** 更新日志列表 */
  changelog?: string[]
  /** 是否强制更新（true 时不可关闭弹窗，只能退出应用） */
  strategy?: boolean
}

/** 将接口返回的版本信息映射为弹窗所需数据（strategy: number → boolean） */
export function toVersionData(data: VersionInfoData): VersionData {
  return {
    version: data.version,
    url: data.url,
    changelog: data.changelog,
    strategy: data.strategy === 1,
  }
}

/** Web 页面路径前缀（这些页面不展示全局版本更新弹窗，如下载页/Ers 子系统/海外支付） */
const WEB_PATH_PREFIXES = ['/ers', '/overseas', '/download']

/** 当前是否为 Web 页面（根路径为下载页也属于 Web 页；读浏览器真实 URL，对齐 React 不依赖路由解析时机） */
function isWebPage(): boolean {
  const { pathname } = window.location
  if (pathname === '/') return true
  return WEB_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/**
 * 应用级版本检测：挂载后检查是否有新版本
 * - Web 页面（下载/Ers/海外支付）跳过，避免与这些页面自身的版本逻辑冲突
 * - 检测失败静默处理，不阻塞用户使用
 */
export function useVersionCheck() {
  // ---- App params ----
  const appParams = useAppParams()

  // ---- State ----
  const visible = shallowRef(false)
  const versionData = shallowRef<VersionData | null>(null)

  /** 检测一次：非 Web 页面且有当前版本号时拉取最新版本，更高则弹窗，失败静默 */
  async function check() {
    const currentVersion = appParams.version
    if (!currentVersion || isWebPage()) return
    try {
      const res = await getVersionInfo()
      const data = res?.data
      if (data?.version && compareVersions(data.version, currentVersion) > 0) {
        versionData.value = toVersionData(data)
        visible.value = true
      }
    } catch {
      // 版本检测失败，静默处理，不阻塞用户使用
    }
  }

  // 应用挂载后检测一次（此时浏览器 URL 与 version 均已就绪，对齐 React useEffect 挂载时检测）
  onMounted(check)

  /** 关闭弹窗（强制更新模式下由组件内部拦截，不会触发） */
  function close() {
    visible.value = false
  }

  return { visible, versionData, close }
}
